import React from "react";
import {
  GridActionsCellItem,
  GridColDef,
  GridPreProcessEditCellProps,
  GridRenderEditCellParams,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowParams,
} from "@mui/x-data-grid";
import { t } from "i18next";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import { ToggleOff, ToggleOn } from "@mui/icons-material";
import { validateEmail, validatePassword } from "./validation";
import {
  ConfirmAction,
  MultipleSelectField,
  Row,
  ToggleActionProps,
} from "../types";
import theme from "../../../components/styles/theme/theme";
import { renderEditInputCell } from "./renderEditInputCell";
import { Region } from "../../../features/regions/types";
import MultipleSelect from "../components/MultipleSelect";
import { SelectChangeEvent } from "@mui/material/Select";
import { Country } from "../../../features/countries/types";
import { isRegion } from "../../../config";

interface Columns {
  rows: Row[];
  rowModesModel: GridRowModel;
  toggleAction: ToggleActionProps;
  regionsData: Region[];
  countriesAll: Country[];
  handleModalOpen: (
    row: GridRowModel<Row>,
    action: ConfirmAction
  ) => () => void;
  handleCancelClick: (id: GridRowId) => () => void;
  handleEditClick: (id: GridRowId) => () => void;
  handleSelectChange: (
    event: SelectChangeEvent<string[]>,
    userId: string,
    type: MultipleSelectField
  ) => void;
}

export const getColumns = ({
  handleModalOpen,
  handleCancelClick,
  handleEditClick,
  handleSelectChange,
  rows,
  rowModesModel,
  toggleAction,
  regionsData,
  countriesAll,
}: Columns): GridColDef[] => {
  const columns = [
    {
      field: "email",
      headerName: "Email",
      width: 280,
      editable: true,
      preProcessEditCellProps: async (params: GridPreProcessEditCellProps) => {
        const errorMessage = await validateEmail(
          params.props.value!.toString()
        );
        return { ...params.props, error: errorMessage };
      },
      renderEditCell: (params: GridRenderEditCellParams) =>
        renderEditInputCell(params, params.error),
    },
    {
      field: "password",
      headerName: "Password",
      width: 150,
      editable: true,
      preProcessEditCellProps: async (
        params: GridPreProcessEditCellProps<Row>
      ) => {
        const errorMessage = await validatePassword(
          params.props.value!.toString(),
          params.row.isNew
        );
        return { ...params.props, error: errorMessage };
      },
      renderEditCell: (params: GridRenderEditCellParams) =>
        renderEditInputCell(params, params.error),
    },
    {
      field: "name",
      headerName: "Name",
      width: 150,
      editable: true,
    },
    {
      field: "surname",
      headerName: "Surname",
      width: 150,
      editable: true,
    },

    {
      field: "region_id",
      headerName: "Region",
      width: 180,
      editable: true,
      type: "actions",
      renderEditCell: ({ row }: GridRenderEditCellParams<Row>) => (
        <MultipleSelect
          rows={rows}
          row={row}
          field="region_id"
          regionsData={regionsData}
          countriesAll={countriesAll}
          handleChange={(event) =>
            handleSelectChange(event, row.id.toString(), "region_id")
          }
          canEdit
        />
      ),
      renderCell: ({ row }: GridRenderEditCellParams<Row>) => (
        <MultipleSelect
          rows={rows}
          row={row}
          regionsData={regionsData}
          countriesAll={countriesAll}
          field="region_id"
        />
      ),
    },
    {
      field: "countries",
      headerName: "Countries",
      cellClassName: "countries",
      type: "actions",
      width: 420,
      editable: true,
      renderEditCell: ({ row }: GridRenderEditCellParams<Row>) => {
        return (
          <MultipleSelect
            rows={rows}
            row={row}
            field="countries"
            handleChange={(event) =>
              handleSelectChange(event, row.id.toString(), "countries")
            }
            countriesAll={countriesAll}
            regionsData={regionsData}
            canEdit={!!row.region_id}
          />
        );
      },
      renderCell: ({ row }: GridRenderEditCellParams<Row>) => {
        return (
          <MultipleSelect
            rows={rows}
            row={row}
            field="countries"
            countriesAll={countriesAll}
            regionsData={regionsData}
          />
        );
      },
    },
    {
      field: "isActive",
      headerName: "Active",
      type: "boolean",
      width: 70,
    },
    {
      field: "isAdmin",
      headerName: "Admin",
      type: "boolean",
      width: 70,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: ({ id, row }: GridRowParams) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
        const commonActions = [
          <GridActionsCellItem
            icon={
              row.isActive ? (
                <ToggleOff color={"error"} />
              ) : (
                <ToggleOn color={"success"} />
              )
            }
            label={t(
              `adminPanel.${row.isActive ? "user_deactivate" : "user_activate"}`
            )}
            onClick={toggleAction(id, "isActive")}
            showInMenu
            disabled={!isInEditMode}
          />,
          <GridActionsCellItem
            icon={
              row.isAdmin ? (
                <ToggleOff color={"error"} />
              ) : (
                <ToggleOn color={"success"} />
              )
            }
            label={t(
              `adminPanel.${
                row.isAdmin ? "admin_deactivate" : "admin_activate"
              }`
            )}
            onClick={toggleAction(id, "isAdmin")}
            showInMenu
            disabled={!isInEditMode}
          />,
        ];

        return isInEditMode
          ? [
              ...commonActions,
              <GridActionsCellItem
                icon={<SaveIcon />}
                label={t("adminPanel.save")}
                sx={{ color: theme.palette.secondary.main }}
                onClick={handleModalOpen(row, "save")}
              />,
              <GridActionsCellItem
                icon={<CancelIcon />}
                label={t("adminPanel.cancel")}
                sx={{ color: theme.palette.primary.main }}
                onClick={handleCancelClick(id)}
              />,
            ]
          : [
              ...commonActions,
              <GridActionsCellItem
                icon={<EditIcon />}
                label={t("adminPanel.edit")}
                onClick={handleEditClick(id)}
                sx={{ color: theme.palette.primary.main }}
              />,
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label={t("adminPanel.delete")}
                onClick={handleModalOpen(row, "delete")}
                sx={{ color: theme.palette.secondary.main }}
              />,
            ];
      },
    },
  ];

  if (countriesAll.length > 1 && !isRegion.usa) {
    return columns;
  } else {
    return columns.filter(
      (column) => column.field !== "countries" && column.field !== "region_id"
    );
  }
};
